import { useQuery } from '@tanstack/react-query';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api, formatBytes, formatRelativeTime } from '../lib/api';
import {
  ArrowLeft,
  HardDrive,
  Clock,
  Trash2,
  File,
  Folder,
  AlertTriangle,
} from 'lucide-react';
import { useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

interface DeviceDetailResponse {
  success: boolean;
  data: {
    id: string;
    name: string;
    hostname: string | null;
    os_info: string | null;
    last_seen: string | null;
    disk_usage: {
      used: number;
      total: number;
      percent: number;
      scanned_at: string;
    } | null;
    created_at: string;
  };
}

interface TopItemsResponse {
  success: boolean;
  data: {
    files: Array<{ path: string; size: number; item_type: string; rank: number }>;
    folders: Array<{ path: string; size: number; item_type: string; rank: number }>;
  };
}

interface SnapshotsResponse {
  success: boolean;
  data: Array<{
    id: string;
    total_disk_size: number;
    used_space: number;
    usage_percent: number;
    scanned_at: string;
  }>;
}

export default function DeviceDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState<'files' | 'folders'>('files');

  const { data: device, isLoading: deviceLoading } = useQuery({
    queryKey: ['device', id],
    queryFn: () => api.get<DeviceDetailResponse>(`/api/devices/${id}`),
  });

  const { data: topItems } = useQuery({
    queryKey: ['device-items', id],
    queryFn: () => api.get<TopItemsResponse>(`/api/devices/${id}/top-items`),
  });

  const { data: snapshots } = useQuery({
    queryKey: ['device-snapshots', id],
    queryFn: () => api.get<SnapshotsResponse>(`/api/devices/${id}/snapshots?limit=100`),
  });

  const handleDelete = async () => {
    if (!id) return;
    setDeleting(true);
    try {
      await api.delete(`/api/devices/${id}`);
      navigate('/dashboard');
    } catch (error) {
      console.error('Delete failed:', error);
      setDeleting(false);
    }
  };

  if (deviceLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!device?.data) {
    return (
      <div className="rounded-2xl bg-red-50 border border-red-200 p-6 text-center">
        <AlertTriangle className="h-10 w-10 text-red-500 mx-auto mb-3" />
        <p className="text-red-700">Device not found</p>
        <Link to="/dashboard" className="text-primary-600 hover:underline mt-2 inline-block">
          Back to Dashboard
        </Link>
      </div>
    );
  }

  const deviceData = device.data;
  const diskPercent = deviceData.disk_usage?.percent || 0;
  const isWarning = diskPercent > 80;
  const isCritical = diskPercent > 90;

  // Prepare chart data
  const chartData = (snapshots?.data || [])
    .slice()
    .reverse()
    .map((s) => ({
      time: new Date(s.scanned_at).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
      }),
      usage: s.usage_percent,
      used: s.used_space,
    }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/dashboard"
            className="rounded-xl bg-neutral-100 p-2.5 text-neutral-600 transition-colors hover:bg-neutral-200"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">{deviceData.name}</h1>
            <p className="text-neutral-500">
              {deviceData.hostname || 'Unknown host'} • {deviceData.os_info || 'Unknown OS'}
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowDeleteModal(true)}
          className="inline-flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-medium text-red-600 transition-colors hover:bg-red-100"
        >
          <Trash2 className="h-4 w-4" />
          Remove Device
        </button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-2xl bg-white p-5 shadow-sm border border-neutral-200/50">
          <div className="flex items-center gap-3">
            <div
              className={`rounded-xl p-2.5 ${
                isCritical
                  ? 'bg-red-50 text-red-600'
                  : isWarning
                    ? 'bg-yellow-50 text-yellow-600'
                    : 'bg-primary-50 text-primary-600'
              }`}
            >
              <HardDrive className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm text-neutral-500">Disk Usage</p>
              <p className="text-xl font-bold text-neutral-900">{diskPercent}%</p>
            </div>
          </div>
        </div>
        <div className="rounded-2xl bg-white p-5 shadow-sm border border-neutral-200/50">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-blue-50 p-2.5 text-blue-600">
              <HardDrive className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm text-neutral-500">Used Space</p>
              <p className="text-xl font-bold text-neutral-900">
                {formatBytes(deviceData.disk_usage?.used || 0)}
              </p>
            </div>
          </div>
        </div>
        <div className="rounded-2xl bg-white p-5 shadow-sm border border-neutral-200/50">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-green-50 p-2.5 text-green-600">
              <HardDrive className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm text-neutral-500">Total Space</p>
              <p className="text-xl font-bold text-neutral-900">
                {formatBytes(deviceData.disk_usage?.total || 0)}
              </p>
            </div>
          </div>
        </div>
        <div className="rounded-2xl bg-white p-5 shadow-sm border border-neutral-200/50">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-neutral-100 p-2.5 text-neutral-600">
              <Clock className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm text-neutral-500">Last Seen</p>
              <p className="text-xl font-bold text-neutral-900">
                {deviceData.last_seen ? formatRelativeTime(deviceData.last_seen) : 'Never'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Usage Chart */}
      {chartData.length > 1 && (
        <div className="rounded-2xl bg-white p-6 shadow-sm border border-neutral-200/50">
          <h2 className="text-lg font-semibold text-neutral-900 mb-4">Disk Usage Over Time</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="time" stroke="#9ca3af" fontSize={12} />
                <YAxis stroke="#9ca3af" fontSize={12} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '12px',
                  }}
                  formatter={(value: number) => [`${value}%`, 'Usage']}
                />
                <Line
                  type="monotone"
                  dataKey="usage"
                  stroke="#8b5cf6"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Top Files & Folders */}
      <div className="rounded-2xl bg-white shadow-sm border border-neutral-200/50">
        <div className="border-b border-neutral-200 p-4">
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('files')}
              className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === 'files'
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-neutral-600 hover:bg-neutral-50'
              }`}
            >
              <File className="h-4 w-4 inline-block mr-2" />
              Top Files
            </button>
            <button
              onClick={() => setActiveTab('folders')}
              className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === 'folders'
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-neutral-600 hover:bg-neutral-50'
              }`}
            >
              <Folder className="h-4 w-4 inline-block mr-2" />
              Top Folders
            </button>
          </div>
        </div>

        <div className="divide-y divide-neutral-100">
          {(activeTab === 'files' ? topItems?.data?.files : topItems?.data?.folders)?.slice(0, 20).map((item, index) => (
            <div key={item.path} className="flex items-center justify-between px-4 py-3 hover:bg-neutral-50">
              <div className="flex items-center gap-3 min-w-0">
                <span className="w-6 text-center text-sm font-medium text-neutral-400">
                  {index + 1}
                </span>
                {activeTab === 'files' ? (
                  <File className="h-4 w-4 text-neutral-400 flex-shrink-0" />
                ) : (
                  <Folder className="h-4 w-4 text-primary-500 flex-shrink-0" />
                )}
                <span className="font-mono text-sm text-neutral-700 truncate">{item.path}</span>
              </div>
              <span className="text-sm font-medium text-neutral-900 flex-shrink-0 ml-4">
                {formatBytes(item.size)}
              </span>
            </div>
          )) || (
            <div className="p-8 text-center text-neutral-500">
              No data available yet
            </div>
          )}
        </div>
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setShowDeleteModal(false)}
          />
          <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <h2 className="text-xl font-bold text-neutral-900 mb-2">Remove Device</h2>
            <p className="text-neutral-600 mb-6">
              Are you sure you want to remove <strong>{deviceData.name}</strong>? This will unlink the device from your account but won't delete its local data.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="rounded-xl bg-neutral-100 px-5 py-2.5 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-200"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="rounded-xl bg-red-600 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

